import axios from 'axios';

import createUrlWithParams from 'helpers/createUrlWithParams';

import METHOD from '../_methods';

import type { AnsweredQuestion, SelfTests } from './_types';
import {
  parseAnswerForBE,
  parseAnswerForFe,
  parseProgressForFE,
  parseSingleSelfTestDetailsForFE,
  parseSelfTestIdForBE,
  SelfTestProgressId,
  parseSelfTestProgressInstanceForFE,
  parseTestResultForFE,
  parseFeedbackTextForFE,
} from './parsers';

const endpoint = 'api/v1/self_tests/';
const progressEndpoint = 'api/v1/self_test_progress/';
const questionInstanceEndpoint = '/api/v1/self_test_question_instances/';

const selfTests: SelfTests = {
  getSelfTests: params => ({ url: createUrlWithParams(endpoint, params), method: METHOD.GET }),
  getAddTestLink: () => ({ url: `${endpoint}link_add/`, method: METHOD.GET }),
};

export const selfTestApi = {
  getSelfTestData: async (id: string) => {
    const { data } = await axios.get(`${endpoint}${id}/`);
    return parseSingleSelfTestDetailsForFE(data);
  },
  getSelectableAssets: async (id: string) => {
    const { data } = await axios.get(`${endpoint}${id}/selectable-assets/`);
    return data;
  },
  createSelfTestInstance: async (selfTestData: SelfTestProgressId) => {
    const { data } = await axios.post(progressEndpoint, parseSelfTestIdForBE(selfTestData));
    return parseProgressForFE(data);
  },
  updateSelfTestMetadata: async ({ id, title, assetId }: { id: number | string; title?: string; assetId?: number | string | null }) => {
    const payload: { title: string; asset_id?: number | string | null } = {
      title: title || '',
    };
    if (assetId !== undefined) payload.asset_id = assetId || null;
    const { data } = await axios.patch(`${progressEndpoint}${id}/metadata/`, payload);
    return data;
  },
  createQuestionInstance: async (data: AnsweredQuestion) => {
    const result = await axios.post(questionInstanceEndpoint, parseAnswerForBE(data));
    return parseAnswerForFe(result.data);
  },
  editQuestionInstance: async (data: AnsweredQuestion) => {
    const dataToSend = {
      answerId: data.answerId,
      questionId: data.questionId,
      userSelfTestId: data.userSelfTestId,
    };
    const result = await axios.patch(`${questionInstanceEndpoint}${data.id}/`, parseAnswerForBE(dataToSend));
    return parseAnswerForFe(result.data);
  },
  getSelfTestProgress: async (id: number | null) => {
    const result = await axios.get(`${progressEndpoint}${id}/`);
    return parseSelfTestProgressInstanceForFE(result.data);
  },
  getTestResult: async (newInstanceId: number | null, existingInstanceId: number | string | null) => {
    const id = newInstanceId || existingInstanceId;
    const result = await axios.get(`${progressEndpoint}${id}/result/`);
    return parseFeedbackTextForFE(result.data);
  },
  getProgressDataAfterFinishingTest: async (newInstanceId: number | null, existingInstanceId: number | string | null) => {
    const id = newInstanceId || existingInstanceId;
    const result = await axios.get(`${progressEndpoint}${id}/completed-result/`);
    return parseTestResultForFE(result.data);
  },
};

export default selfTests;
